import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldWarning, Crosshair } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { API_URL } from '../config/api';

/**
 * Dashboard CTA prompting users to confirm sensor placement so TurfPulse Guardian
 * can transition from Advisory Mode → Live Auto-Fire. Self-hides for users who
 * haven't connected Ecowitt yet (avoids dunking on free / non-hardware users)
 * AND for users who've already confirmed placement.
 */
export const SensorPlacementCTA = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/sensors/placement`, { withCredentials: true });
        if (alive) setData(res.data);
      } catch (e) {
        // 401 / 403 / 404 — silently hide CTA
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!loaded || !data) return null;
  // Don't show CTA if Ecowitt isn't connected — placement isn't actionable yet
  if (!data.ecowitt_connected) return null;
  // Already confirmed → quiet success badge instead of dunking nudge
  if (data.sensors_placement_confirmed) {
    return (
      <div
        className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3"
        data-testid="hub-guardian-active"
      >
        <ShieldCheck size={28} weight="duotone" className="text-emerald-600 dark:text-emerald-400 shrink-0" />
        <div className="flex-1">
          <div className="font-medium text-sm text-foreground">
            Guardian: Live Auto-Fire active
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Schedules will fire automatically based on your buried probes.
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/sensors/placement')}
          data-testid="hub-guardian-edit-btn"
        >
          Edit
        </Button>
      </div>
    );
  }

  // Advisory mode — prompt to confirm placement
  return (
    <div
      className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
      data-testid="hub-placement-cta"
    >
      <div className="flex items-start gap-3 flex-1">
        <ShieldWarning size={36} weight="duotone" className="text-amber-600 dark:text-amber-400 shrink-0" />
        <div>
          <div className="font-semibold text-base text-foreground">
            Confirm sensor placement to unlock Auto-Fire
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Guardian is in <strong>Advisory Mode</strong>. Map your buried Ecowitt probes (DRY / AVG / WET) so schedules can fire on real soil moisture instead of forecast guesses.
          </div>
        </div>
      </div>
      <Button
        onClick={() => navigate('/sensors/placement')}
        className="shrink-0"
        data-testid="hub-placement-cta-btn"
      >
        <Crosshair size={16} className="mr-2" weight="duotone" />
        Map Probes
      </Button>
    </div>
  );
};
